import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Table, Badge, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { getAugmentationJobDetail } from '../services/api/augmentationApi';
import { getReviewJobDetail, resolveAugmentationJob } from '../services/api/augmentationReviewApi';
import JobDetailModal from './augmentation-review/JobDetailModal';
import { useAuth } from '../contexts/AuthContext';
import { usePageBreadcrumbs } from '../hooks/usePageBreadcrumbs';

const AugmentationJobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewJobDetail, setReviewJobDetail] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [success, setSuccess] = useState('');

  const jobLabel = job?.customerName || job?.id || jobId || 'Loading...';
  usePageBreadcrumbs([
    { label: 'Augmentation Jobs', path: '/augmentation-jobs' },
    { label: jobLabel }
  ], [jobLabel]);

  const fetchJobDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAugmentationJobDetail(jobId);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching augmentation job details:', error);
      if (error.response?.status === 404) {
        setError('Augmentation job not found');
      } else if (error.response?.status === 400) {
        // Job exists but is not viewable (e.g., COMPLETED status)
        const errorMessage = error.response?.data || error.message || '';
        setError(`This augmentation job is not available for review. ${errorMessage}`);
      } else {
        setError('Failed to load augmentation job details');
      }
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const getStatusBadge = (status) => {
    const variants = {
      'COMPLETED': 'success',
      'NEEDS_REVIEW_AUGMENTATION': 'warning',
      'FAILED': 'danger',
      'PROCESSING': 'primary',
      'REQUESTED': 'info'
    };
    
    const variant = variants[status] || 'secondary';
    return <Badge bg={variant}>{status}</Badge>;
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds) return 'N/A';
    return `${(milliseconds / 1000).toFixed(2)}s`;
  };

  const handleStartReview = async () => {
    if (!job || (job.status !== 'NEEDS_REVIEW_AUGMENTATION' && job.status !== 'FAILED')) return;
    
    try {
      setLoadingReview(true);
      const response = await getReviewJobDetail(jobId);
      setReviewJobDetail(response.data);
      setShowReviewModal(true);
    } catch (error) {
      console.error('Error fetching review job detail:', error);
      setError('Failed to load job details for review');
    } finally {
      setLoadingReview(false);
    }
  };

  const handleCloseReview = () => {
    setShowReviewModal(false);
    setReviewJobDetail(null);
  };

  const handleResolveJob = async (resolution) => {
    if (!reviewJobDetail) return;
    
    try {
      await resolveAugmentationJob(reviewJobDetail.id, resolution);
      setSuccess('Job resolved successfully!');
      setShowReviewModal(false);
      setReviewJobDetail(null);
      
      // Refresh the job details to show updated status
      await fetchJobDetails();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error resolving job:', error);
      setError('Failed to resolve job');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading augmentation job details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Augmentation Job Details</h2>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left me-1"></i>Back
          </Button>
        </div>
        
        <Alert variant="warning">
          <Alert.Heading>Limited Information Available</Alert.Heading>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            <strong>Available options:</strong>
          </p>
          <ul className="mt-2 mb-0">
            <li>Return to the extraction job to see basic augmentation status</li>
            <li>Check the augmentation review queue if this job requires manual review</li>
            <li>Contact support if you need access to detailed augmentation logs</li>
          </ul>
        </Alert>
        
        <div className="d-flex gap-2 mt-3">
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left me-1"></i>Go Back
          </Button>
          <Button 
            variant="outline-primary" 
            onClick={() => navigate('/augmentation/review-queue')}
          >
            <i className="bi bi-list-check me-1"></i>Review Queue
          </Button>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div>
        <Alert variant="warning">Augmentation job not found</Alert>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-1"></i>Go Back
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Augmentation Job Details</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left me-1"></i>Back
          </Button>
          {job.extractionJobId && (
            <Button 
              as={Link}
              to={`/extraction-jobs/${job.extractionJobId}`}
              variant="outline-primary"
            >
              <i className="bi bi-file-text me-1"></i>View Extraction Job
            </Button>
          )}
          {(job.status === 'NEEDS_REVIEW_AUGMENTATION' || job.status === 'FAILED') && hasPermission('augmentation_review', 'view') && (
            <Button 
              variant={job.status === 'FAILED' ? 'danger' : 'warning'}
              onClick={handleStartReview}
              disabled={loadingReview}
            >
              {loadingReview ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Loading...
                </>
              ) : (
                <>
                  <i className={job.status === 'FAILED' ? 'bi bi-exclamation-circle me-1' : 'bi bi-exclamation-triangle me-1'}></i>
                  {job.status === 'FAILED' ? 'View Details' : 'Start Review'}
                </>
              )}
            </Button>
          )}
          {(job.status === 'NEEDS_REVIEW_AUGMENTATION' || job.status === 'FAILED') && (
            <Button 
              as={Link}
              to={`/augmentation/review-queue`}
              variant="outline-secondary"
            >
              <i className="bi bi-list-check me-1"></i>Review Queue
            </Button>
          )}
        </div>
      </div>

      {success && <Alert variant="success">{success}</Alert>}

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Job Information</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Table borderless>
                <tbody>
                  <tr>
                    <td><strong>Job ID:</strong></td>
                    <td>{job.id}</td>
                  </tr>
                  <tr>
                    <td><strong>Status:</strong></td>
                    <td>{getStatusBadge(job.status)}</td>
                  </tr>
                  <tr>
                    <td><strong>Created:</strong></td>
                    <td>{new Date(job.createdAt).toLocaleString()}</td>
                  </tr>
                  {job.completedAt && (
                    <tr>
                      <td><strong>Completed:</strong></td>
                      <td>{new Date(job.completedAt).toLocaleString()}</td>
                    </tr>
                  )}
                  <tr>
                    <td><strong>Customer:</strong></td>
                    <td>{job.customerName || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Extraction Job ID:</strong></td>
                    <td>
                      {job.extractionJobId ? (
                        <Link to={`/extraction-jobs/${job.extractionJobId}`}>
                          {job.extractionJobId}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <Table borderless>
                <tbody>
                  <tr>
                    <td><strong>Rules Executed:</strong></td>
                    <td>{job.rulesExecuted || 0}</td>
                  </tr>
                  <tr>
                    <td><strong>Processing Time:</strong></td>
                    <td>{formatDuration(job.totalProcessingTimeMs)}</td>
                  </tr>
                  {job.statusReason && (
                    <tr>
                      <td><strong>Status Reason:</strong></td>
                      <td>{job.statusReason}</td>
                    </tr>
                  )}
                  {job.errorMessage && (
                    <tr>
                      <td><strong>Error:</strong></td>
                      <td className="text-danger">{job.errorMessage}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Processing Results */}
      {job.resultsJson && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Processing Results</h5>
          </Card.Header>
          <Card.Body>
            <div className="bg-light p-3" style={{ maxHeight: '400px', overflow: 'auto' }}>
              <pre>{JSON.stringify(JSON.parse(job.resultsJson), null, 2)}</pre>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Rule Execution Details */}
      {job.ruleExecutions && job.ruleExecutions.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Rule Execution Details</h5>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Rule Name</th>
                  <th>Status</th>
                  <th>Processing Time</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {job.ruleExecutions.map((execution, index) => (
                  <tr key={index}>
                    <td>{execution.ruleName}</td>
                    <td>
                      <Badge bg={execution.success ? 'success' : 'danger'}>
                        {execution.success ? 'Success' : 'Failed'}
                      </Badge>
                    </td>
                    <td>{formatDuration(execution.processingTimeMs)}</td>
                    <td>
                      {execution.result ? (
                        <div style={{ maxHeight: '100px', overflow: 'auto' }}>
                          <pre style={{ fontSize: '0.8em' }}>
                            {JSON.stringify(execution.result, null, 2)}
                          </pre>
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Processing Logs */}
      {job.logsJson && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Processing Logs</h5>
          </Card.Header>
          <Card.Body>
            <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
              {(() => {
                try {
                  const logs = JSON.parse(job.logsJson);
                  return logs.map((log, index) => (
                    <div key={index} className="mb-1">
                      <small>
                        [{new Date(log.Timestamp).toLocaleString()}] [{log.Level}] {log.Message}
                      </small>
                    </div>
                  ));
                } catch (error) {
                  return <p className="text-warning">Error parsing logs: {error.message}</p>;
                }
              })()}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Review Modal */}
      {showReviewModal && reviewJobDetail && (
        <JobDetailModal
          show={showReviewModal}
          job={reviewJobDetail}
          onClose={handleCloseReview}
          onResolve={handleResolveJob}
          loading={loadingReview}
        />
      )}
    </div>
  );
};

export default AugmentationJobDetail;